import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Bug, RefreshCw, Copy, Check } from "lucide-react";
import { useBlueJayRulesRead, type RawPolicyData } from "@/hooks/useBlueJayRulesRead";

interface PolicyDebugCardProps {
  deviceId: string;
}

export const PolicyDebugCard = ({ deviceId }: PolicyDebugCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { rawPolicy, isLoading, error, refetch } = useBlueJayRulesRead(deviceId);

  const handleCopy = async () => {
    if (!rawPolicy) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(rawPolicy, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (isLoading) {
    return (
      <Card className="border-dashed border-amber-300 bg-amber-50/50">
        <CardContent className="py-4">
          <div className="flex items-center gap-2 text-amber-600">
            <Bug className="w-4 h-4" />
            <span className="text-sm">Loading policy debug data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-dashed border-amber-300 bg-amber-50/50">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="py-3">
          <CollapsibleTrigger className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <Bug className="w-4 h-4 text-amber-600" />
              <CardTitle className="text-sm font-medium text-amber-800">
                Policy Debug View
              </CardTitle>
              {error && <Badge variant="destructive" className="text-xs">Error</Badge>}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  refetch();
                }}
              >
                <RefreshCw className="w-3 h-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
              >
                {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
              </Button>
            </div>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {rawPolicy && (
              <>
                {/* Connection Info */}
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Connection</h4>
                  <div className="bg-white rounded p-2 text-xs font-mono space-y-1">
                    <div><span className="text-gray-500">Serial:</span> {rawPolicy.serial}</div>
                    <div className="break-all"><span className="text-gray-500">Source:</span> {rawPolicy.source}</div>
                    {rawPolicy.policyUrl && (
                      <div className="break-all"><span className="text-gray-500">URL:</span> {rawPolicy.policyUrl}</div>
                    )}
                  </div>
                </div>

                {/* Parse Notes */}
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Parse Notes</h4>
                  <ul className="bg-white rounded p-2 text-xs space-y-0.5">
                    {rawPolicy.parseNotes.map((note, i) => (
                      <li key={i} className="text-gray-600">• {note}</li>
                    ))}
                  </ul>
                </div>

                {/* Parsed Result Summary */}
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Parsed Result</h4>
                  <div className="bg-white rounded p-2 text-xs font-mono">
                    {rawPolicy.parsedResult === false ? (
                      <div className="text-red-600 font-semibold">Browser Disabled (false)</div>
                    ) : rawPolicy.parsedResult === null ? (
                      <div className="text-gray-400">null (loading or error)</div>
                    ) : (
                      <div className="space-y-1">
                        <div>
                          <span className="text-gray-500">Categories:</span>{" "}
                          <span className={rawPolicy.parsedResult.categories.length > 0 ? "text-green-600" : "text-gray-400"}>
                            [{rawPolicy.parsedResult.categories.length}] {rawPolicy.parsedResult.categories.slice(0, 3).join(", ")}
                            {rawPolicy.parsedResult.categories.length > 3 && "..."}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Allowlist:</span>{" "}
                          <span className={rawPolicy.parsedResult.allowlist.length > 0 ? "text-green-600" : "text-gray-400"}>
                            [{rawPolicy.parsedResult.allowlist.length}] {rawPolicy.parsedResult.allowlist.slice(0, 3).join(", ")}
                            {rawPolicy.parsedResult.allowlist.length > 3 && "..."}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Blocklist:</span>{" "}
                          <span className={rawPolicy.parsedResult.blocklist.length > 0 ? "text-green-600" : "text-gray-400"}>
                            [{rawPolicy.parsedResult.blocklist.length}] {rawPolicy.parsedResult.blocklist.slice(0, 3).join(", ")}
                            {rawPolicy.parsedResult.blocklist.length > 3 && "..."}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Config:</span>{" "}
                          nsfwLevel={rawPolicy.parsedResult.config.nsfwLevel}, 
                          human={String(rawPolicy.parsedResult.config.human)}, 
                          accuracy={rawPolicy.parsedResult.config.accuracy},
                          blockVideos={String(rawPolicy.parsedResult.config.blockVideos)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Raw Response */}
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Raw Response</h4>
                  <pre className="bg-gray-900 text-green-400 rounded p-2 text-xs overflow-auto max-h-64">
                    {rawPolicy.rawResponse 
                      ? JSON.stringify(rawPolicy.rawResponse, null, 2)
                      : "No response (error occurred)"}
                  </pre>
                </div>
              </>
            )}

            {error && (
              <div className="bg-red-100 rounded p-2 text-xs text-red-700">
                <strong>Error:</strong> {error.message}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
